/**
 * HeroSlideshow Component - Premium Design
 *
 * Slideshow profissional com perspectiva 3D, glow effects,
 * e apresentação elegante das features do sistema.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import { landingColors } from '../../theme/landingTheme';

interface Slide {
  image: string;
  title: string;
  description?: string;
  tag?: string;
  tagColor?: string;
}

interface HeroSlideshowProps {
  slides: Slide[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showControls?: boolean;
  showDots?: boolean;
  showCaptions?: boolean;
  showPlayPause?: boolean;
  height?: string | number;
  borderRadius?: number;
}

const HeroSlideshow: React.FC<HeroSlideshowProps> = ({
  slides,
  autoPlay = true,
  autoPlayInterval = 5000,
  showControls = true,
  showDots = true,
  showCaptions = true,
  showPlayPause = true,
  height = 500,
  borderRadius = 20,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isHovered, setIsHovered] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isPlaying, autoPlayInterval, slides.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const goToIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const togglePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === ' ') {
        e.preventDefault();
        togglePlayPause();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext, togglePlayPause]);

  const handleImageError = (index: number) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  // Filter out slides with errors
  const validSlides = slides.filter((_, i) => !imageErrors[i]);

  if (validSlides.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: typeof height === 'number' ? height : height,
          bgcolor: 'rgba(27, 106, 99, 0.05)',
          borderRadius: `${borderRadius}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed rgba(27, 106, 99, 0.2)',
        }}
      >
        <Typography sx={{ color: landingColors.neutral.gray400 }}>
          Screenshots do sistema em breve
        </Typography>
      </Box>
    );
  }

  const responsiveHeight = isMobile ? 300 : isTablet ? 400 : height;

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: 'relative',
        width: '100%',
        height: responsiveHeight,
        // Perspective container for 3D effect
        perspective: '1200px',
        perspectiveOrigin: 'center center',
      }}
    >
      {/* Glow Effect Behind Device */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '120%',
          height: '120%',
          background: `radial-gradient(ellipse at center, rgba(27, 106, 99, 0.25) 0%, rgba(39, 171, 131, 0.15) 30%, transparent 70%)`,
          filter: 'blur(40px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Main Device Container with 3D Transform */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transform: isHovered
            ? 'rotateY(-2deg) rotateX(1deg) scale(1.02)'
            : 'rotateY(-4deg) rotateX(2deg)',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 1,
        }}
      >
        {/* Device Frame - Monitor/Laptop Style */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '16px',
            overflow: 'hidden',
            bgcolor: '#1a1a1a',
            boxShadow: `
              0 50px 100px -20px rgba(0, 0, 0, 0.5),
              0 30px 60px -30px rgba(0, 0, 0, 0.4),
              0 0 0 1px rgba(255, 255, 255, 0.08),
              inset 0 1px 0 rgba(255, 255, 255, 0.1)
            `,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Browser Top Bar - Premium Design */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: { xs: 1.5, md: 2.5 },
              py: { xs: 0.75, md: 1 },
              bgcolor: '#2a2a2a',
              borderBottom: '1px solid #3a3a3a',
              minHeight: { xs: 32, md: 40 },
            }}
          >
            {/* Traffic Lights */}
            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
              <Box sx={{
                width: { xs: 10, md: 12 },
                height: { xs: 10, md: 12 },
                borderRadius: '50%',
                bgcolor: '#ff5f57',
                boxShadow: 'inset 0 -1px 1px rgba(0,0,0,0.2)',
              }} />
              <Box sx={{
                width: { xs: 10, md: 12 },
                height: { xs: 10, md: 12 },
                borderRadius: '50%',
                bgcolor: '#febc2e',
                boxShadow: 'inset 0 -1px 1px rgba(0,0,0,0.2)',
              }} />
              <Box sx={{
                width: { xs: 10, md: 12 },
                height: { xs: 10, md: 12 },
                borderRadius: '50%',
                bgcolor: '#28c840',
                boxShadow: 'inset 0 -1px 1px rgba(0,0,0,0.2)',
              }} />
            </Box>

            {/* URL Bar */}
            <Box
              sx={{
                flex: 1,
                mx: { xs: 1, md: 3 },
                px: { xs: 1.5, md: 2 },
                py: 0.5,
                bgcolor: '#1a1a1a',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                maxWidth: 300,
              }}
            >
              {/* Lock Icon */}
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: '#28c840',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Box sx={{
                  width: 6,
                  height: 6,
                  bgcolor: '#1a1a1a',
                  borderRadius: '50%'
                }} />
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: '0.65rem', md: '0.75rem' },
                  color: '#999',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                }}
              >
                app.clinafy.com.br
              </Typography>
            </Box>

            {/* Spacer for symmetry */}
            <Box sx={{ width: { xs: 42, md: 54 }, flexShrink: 0 }} />
          </Box>

          {/* Screenshot Content Area */}
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              overflow: 'hidden',
              bgcolor: '#0f0f14',
            }}
          >
            {/* Slides */}
            {slides.map((slide, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: index === currentIndex ? 1 : 0,
                  transform: index === currentIndex
                    ? 'scale(1) translateX(0)'
                    : 'scale(0.98) translateX(10px)',
                  transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                  pointerEvents: 'none', // Slides não capturam cliques - botões ficam acessíveis
                }}
              >
                {!imageErrors[index] && (
                  <Box
                    component="img"
                    src={slide.image}
                    alt={slide.title}
                    loading="lazy"
                    decoding="async"
                    onError={() => handleImageError(index)}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'top center',
                    }}
                  />
                )}
              </Box>
            ))}

            {/* Gradient Overlay for Captions */}
            {showCaptions && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '50%',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
                  pointerEvents: 'none',
                }}
              />
            )}

            {/* Caption Card - Floating Design */}
            {showCaptions && slides[currentIndex] && (
              <Box
                key={`caption-${currentIndex}`}
                sx={{
                  position: 'absolute',
                  bottom: { xs: 60, md: 70 },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: { xs: '90%', md: '80%' },
                  maxWidth: 400,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 3,
                  px: { xs: 2, md: 3 },
                  py: { xs: 1.5, md: 2 },
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)',
                  textAlign: 'center',
                  pointerEvents: 'none', // Permite cliques passarem para os botões
                  animation: 'fadeSlideUp 0.6s ease-out',
                  '@keyframes fadeSlideUp': {
                    from: {
                      opacity: 0,
                      transform: 'translateX(-50%) translateY(20px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateX(-50%) translateY(0)',
                    },
                  },
                }}
              >
                {slides[currentIndex].tag && (
                  <Chip
                    label={slides[currentIndex].tag}
                    size="small"
                    sx={{
                      mb: 1,
                      bgcolor: slides[currentIndex].tagColor || landingColors.primary.main,
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 22,
                      letterSpacing: '0.05em',
                    }}
                  />
                )}
                <Typography
                  variant="h6"
                  sx={{
                    color: '#1a1a1a',
                    fontWeight: 700,
                    fontSize: { xs: '1rem', md: '1.15rem' },
                    lineHeight: 1.3,
                  }}
                >
                  {slides[currentIndex].title}
                </Typography>
                {slides[currentIndex].description && (
                  <Typography
                    sx={{
                      color: '#666',
                      fontSize: { xs: '0.75rem', md: '0.85rem' },
                      mt: 0.5,
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    {slides[currentIndex].description}
                  </Typography>
                )}
              </Box>
            )}

          </Box>
        </Box>

        {/* Progress Dots - Below Device */}
        {showDots && slides.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: -30, md: -40 },
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              zIndex: 10,
            }}
          >
            {slides.map((_, index) => (
              <Box
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  goToIndex(index);
                }}
                sx={{
                  width: index === currentIndex ? 28 : 10,
                  height: 10,
                  borderRadius: 5,
                  bgcolor: index === currentIndex
                    ? '#FFFFFF'
                    : 'rgba(255,255,255,0.35)',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: index === currentIndex
                    ? '0 0 12px rgba(255,255,255,0.6)'
                    : 'none',
                  border: index === currentIndex
                    ? '2px solid rgba(255,255,255,0.9)'
                    : '2px solid transparent',
                  '&:hover': {
                    bgcolor: index === currentIndex
                      ? '#FFFFFF'
                      : 'rgba(255,255,255,0.6)',
                    transform: 'scale(1.15)',
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Reflection Effect */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: -60, md: -80 },
          left: '5%',
          right: '5%',
          height: { xs: 40, md: 60 },
          background: `linear-gradient(to bottom, rgba(27, 106, 99, 0.1), transparent)`,
          filter: 'blur(20px)',
          transform: 'scaleY(-0.3) rotateX(180deg)',
          pointerEvents: 'none',
          opacity: 0.5,
        }}
      />

      {/* Navigation Arrows - FORA do container 3D para funcionar corretamente */}
      {showControls && slides.length > 1 && (
        <>
          <IconButton
            onClick={goToPrevious}
            aria-label="Slide anterior"
            sx={{
              position: 'absolute',
              left: { xs: 8, md: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#FFFFFF',
              bgcolor: 'rgba(0,0,0,0.7)',
              zIndex: 100,
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.9)',
              },
              '&:active': {
                bgcolor: 'rgba(27, 106, 99, 1)',
              },
              width: { xs: 36, md: 44 },
              height: { xs: 36, md: 44 },
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          >
            <ChevronLeftIcon fontSize="medium" />
          </IconButton>
          <IconButton
            onClick={goToNext}
            aria-label="Próximo slide"
            sx={{
              position: 'absolute',
              right: { xs: 8, md: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#FFFFFF',
              bgcolor: 'rgba(0,0,0,0.7)',
              zIndex: 100,
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.9)',
              },
              '&:active': {
                bgcolor: 'rgba(27, 106, 99, 1)',
              },
              width: { xs: 36, md: 44 },
              height: { xs: 36, md: 44 },
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          >
            <ChevronRightIcon fontSize="medium" />
          </IconButton>
        </>
      )}

      {/* Play/Pause Button - FORA do container 3D */}
      {showPlayPause && slides.length > 1 && (
        <IconButton
          onClick={togglePlayPause}
          aria-label={isPlaying ? 'Pausar slideshow' : 'Reproduzir slideshow'}
          sx={{
            position: 'absolute',
            top: { xs: 8, md: 12 },
            right: { xs: 8, md: 16 },
            color: '#FFFFFF',
            bgcolor: isPlaying ? 'rgba(0,0,0,0.7)' : 'rgba(27, 106, 99, 0.95)',
            zIndex: 100,
            '&:hover': {
              bgcolor: isPlaying ? 'rgba(0,0,0,0.9)' : 'rgba(27, 106, 99, 1)',
            },
            '&:active': {
              bgcolor: 'rgba(27, 106, 99, 1)',
            },
            width: { xs: 32, md: 40 },
            height: { xs: 32, md: 40 },
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {isPlaying ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
        </IconButton>
      )}
    </Box>
  );
};

export default HeroSlideshow;
